import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { ReactNode, useEffect } from 'react';
import { cx, twc } from 'react-twc';

import { useFortune, useFortuneUpdate } from '@/api-hooks/useFortune';
import { useCreateOfflineShop } from '@/api-hooks/useOfflineShops';
import { useUser } from '@/api-hooks/useUser';
import FortuneCanvas from '@/components/FortuneCanvas';
import SpeechText from '@/components/SpeechText';
import Image from '@/components/v2/Image';
import { CLIENT_URL } from '@/consts/currentUrl';
import useAlert from '@/hooks/useAlert';
import { useCopy } from '@/hooks/useCopy';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useReferralCode } from '@/hooks/useReferral';
import { useSessionReferral } from '@/hooks/useSessionReferral';
import { Fortune } from '@/types/fortune';
import { cn, twx } from '@/utils/twx';

import styles from './fortune.module.css';
import { Deco } from './svgs';
import {
  FORTUNE_FORM_REGISTER_STATUS,
  useFortuneFormData,
  useFortuneFormStatus,
  useFortuneStep,
} from './useFortune';

const gift = {
  3: '초대박',
  2: '왕대박',
  1: '대박',
};

const FortunePage3 = ({ fortune }: { fortune: Fortune }) => {
  const { data: referralCode } = useReferralCode();
  const [{ shopName, openedAt, shopAddress, category }, setFortuneFormData] = useFortuneFormData();
  const [, { reset }] = useFortuneStep();
  const [referral, setReferral] = useSessionReferral();

  const { mutate: updateFortune } = useFortuneUpdate();
  const { mutate: createOfflineShop } = useCreateOfflineShop();
  const { data: user } = useUser();
  const [status, setStatus] = useFortuneFormStatus();
  const showAlert = useAlert();
  const router = useRouter();

  const [, copy] = useCopy();

  const { onClickShareFortune } = useKakaoShare();

  const handleClickSignUp = () => {
    // referral 없으면 운세몽
    if (!referral) {
      setReferral('운세몽');
    }

    router.push('/auth/event/sign-up');
  };

  const handleClickCopy = () => {
    const addUrl = referralCode?.referral_link ? '/' + referralCode?.referral_link : '';

    copy(CLIENT_URL + '/fortune' + addUrl);
    showAlert('링크가 복사되었습니다');
  };

  const kakaoShare = () => {
    onClickShareFortune({
      domain: CLIENT_URL,
      path: '/fortune/' + referralCode?.referral_link || '',
    });
  };

  const resetFortuneFormData = () => {
    setFortuneFormData({
      shopName: '',
      shopAddress: '',
      openedAt: '',
      category: '',
    });
    setStatus(FORTUNE_FORM_REGISTER_STATUS.NONE);

    reset();
  };

  useEffect(() => {
    if (!user) return;
    if (user && !fortune.userId) {
      updateFortune(fortune.fortuneId);
      createOfflineShop({
        shop_name: shopName,
        shop_address: shopAddress,
        category,
        crawl: true,
      });
    }

    if (status === FORTUNE_FORM_REGISTER_STATUS.NEW) {
      showAlert('이벤트 참여가 완료되었습니다. 리워드는 2월 14일 일괄 지급됩니다');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fortune]);

  useEffect(() => {
    if (!FORTUNE_FORM_REGISTER_STATUS.SIGNIN && user?.genderNumber) {
      showAlert('해당 이벤트는 신규 사장님을 위한 이벤트로, 참여 대상이 아닙니다');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <section className={cx('py-5 px-4', styles.bg)}>
        <div className="flex flex-col gap-5">
          {user?.user_id ? (
            <Image
              src={'/img/fortune/result/fortune-result-banner-2.png'}
              width={385}
              height={159}
              alt="운명을 알면... 성공이 보인다!"
              className="w-full h-auto"
            />
          ) : (
            <Image
              src={'/img/fortune/result/fortune-result-banner.png'}
              width={385}
              height={159}
              alt="미래가 보인다몽"
              className="w-full h-auto"
            />
          )}

          <InnerContainer>
            <InnerTitle>
              {shopName} 오픈일: {dayjs(openedAt).format('YYYY/MM/DD')}
            </InnerTitle>
            <InnerContent className="mt-2.5">{fortune.fortunePrediction}</InnerContent>
          </InnerContainer>
          <InnerContainer>
            <InnerTitle>{shopName}의 2025년 운세 분석 </InnerTitle>
            <InnerContent className="mt-5 flex flex-col gap-2.5">
              <div>
                <ContentTitle>전반적인 운세:</ContentTitle>
                <Content>{fortune.overallFortune}</Content>
              </div>
              <div>
                <ContentTitle>1. 재물운:</ContentTitle>
                <Content>{fortune.financialFortune}</Content>
              </div>

              <div>
                <ContentTitle>2. 고객과의 관계:</ContentTitle>
                <Content>{fortune.customerFortune}</Content>
              </div>
              <div>
                <ContentTitle>3. 운영 효율성: </ContentTitle>
                <Content>{fortune.optionalEfficiency}</Content>
              </div>
            </InnerContent>
          </InnerContainer>

          <InnerContainer className="flex flex-col gap-5 px-[30px] relative">
            <InnerTitle>{shopName}의 2025년 운세 총평 </InnerTitle>
            <div className="">
              {/* Image, 아랫부분 offset 하게 적용 */}
              <SubTitle>2025 을사년</SubTitle>

              <Title>
                올해 장사 운세 <span className={cx('text-[#F67C38]')}>{gift[fortune.lottery]}</span>
              </Title>
            </div>

            <Content className="pl-0">{fortune.futureOfYears}</Content>

            <div className="absolute -bottom-6 left-0 w-full flex justify-center">
              <button
                className="underline text-[14px] text-gray-2 leading-none"
                onClick={resetFortuneFormData}
                data-gtm-id="fortune-result-another-shop"
              >
                다른 매장 운세도 확인하기
              </button>
            </div>
          </InnerContainer>

          <InnerContainer className="mt-6">
            <InnerTitle>{shopName}의 2025년 행운 아이템 </InnerTitle>

            <div className="mt-6 flex justify-center gap-2.5">
              <div>
                <SubTitle>댓글 관리 시간을 90% 줄여주는</SubTitle>
                <Title className="text-[#35373E]">
                  AI 비서 <span className="text-[#F67C38]">댓글몽</span>
                </Title>
              </div>
              <div>
                <Image src={'/img/fortune/mong.png'} width={60} height={56} alt="mong" />
              </div>
            </div>

            <hr className="border-white my-[25px] w-[104%] -translate-x-[2%]" />

            <p className="text-gray-1 leading-[1.4] text-balance text-center">
              <b className="font-semibold">모든 배달 앱의 리뷰</b>를 한 곳에서 확인하고, <br /> 리뷰
              내용에 맞는 <b className="font-semibold">AI 초안 답변 3종</b>을 골라써요!
            </p>
            <div className="mt-2.5">
              <Image
                src={'/img/fortune/result/fortune-review.png'}
                width={290}
                height={164}
                alt="댓글몽-샘플"
                className="w-full h-auto"
              />
            </div>
          </InnerContainer>
        </div>
        <div>
          {(!user?.user_id || status === FORTUNE_FORM_REGISTER_STATUS.NEW) && (
            <div>
              <div className="text-center kcc-ganpan mt-[53px] mb-[40px]">
                <p className="text-secondary kcc-ganpan text-[18px] leading-[22px]">
                  여기서 끝이 아니에요!
                </p>

                <p className={cx('mt-3.5 text-white text-[30px] leading-[41px]', styles.shadow)}>
                  신규 사장님을 위한
                </p>
                <p className="-mt-1 text-[#121212] text-[40px] leading-[55px] tracking-[-2px]">
                  새해 추가 이벤트
                </p>
              </div>

              <DecoInner>
                <div className="my-[10px] text-center text-balance">
                  <p className="text-[20px] leading-[22px] text-secondary kcc-ganpan tracking-[-0.3px]">
                    꽝 없는 100% 당첨
                  </p>
                  <p className="text-[#35373E] text-[40px] leading-[44px] kcc-ganpan tracking-[-0.3px]">
                    복권 긁어 보기
                  </p>

                  <p className="text-[13px] text-[#35373E] leading-[1.5] my-5">
                    CU 상품권, 스타벅스 기프티콘부터 <br />
                    <b>신세계 상품권 10,000원</b>
                    까지 상품이 팡팡팡~!
                  </p>
                  <div className="">
                    <FortuneCanvas
                      src={`/img/fortune/result/gift-${fortune.lottery}.png`}
                      fortuneId={fortune.fortuneId}
                    />
                  </div>
                  <p
                    className={cx(
                      'text-[#35373E80] text-[12px] leading-[19px]',
                      'tracking-[-0.36px]',
                      'mt-2.5',
                    )}
                  >
                    * 리워드는 회원 가입 및 매장 등록 이후 2월 14일 일괄 지급됩니다.
                  </p>
                </div>
              </DecoInner>

              <div className="mt-[30px]">
                <button
                  className={cx(
                    styles['btn'],
                    'text-[#121212] py-5 w-full text-[18px] font-bold leading-[1.5]',
                  )}
                  onClick={handleClickSignUp}
                  data-gtm-id="fortune-result-signup"
                >
                  댓글몽 회원 가입하고 상품 받아가기
                </button>
              </div>
            </div>
          )}
          <div className="mt-[75px] kcc-ganpan text-center">
            <p className="text-secondary text-[18px] leading-tight">복채는 이걸로 받을게요!</p>
            <p className={cx('text-white text-[30px] leading-[41px]', styles.shadow)}>
              친구의 매장 운세는?
            </p>
            <p className="text-[#121212] text-[40px] tracking-[-2px] leading-[55px] -mt-1">
              주위에 공유하기
            </p>
          </div>
          <DecoInner className="mt-10">
            <div className="flex justify-center gap-10 text-center px-[23px]">
              <button
                className="flex flex-col gap-[10px] items-center justify-center w-[80px] *:pointer-events-none"
                onClick={kakaoShare}
                data-gtm-id="fortune-result-share-kakao"
              >
                <Image
                  src={'/img/fortune/result/share-kakao.png'}
                  width={50}
                  height={50}
                  alt="share-kakao"
                  className="rounded-full mx-auto"
                />
                <p className="text-[#35373E] font-bold text-[14px]">카카오톡 공유</p>
              </button>

              <button
                className="flex flex-col gap-[10px] items-center justify-center w-[80px] *:pointer-events-none"
                onClick={handleClickCopy}
                data-gtm-id="fortune-result-share-link"
              >
                <Image
                  src={'/img/fortune/result/share-link.png'}
                  width={50}
                  height={50}
                  alt="share-link"
                  className="rounded-full mx-auto"
                />
                <p className="text-[#35373E] font-bold text-[14px]">링크 공유</p>
              </button>
            </div>
          </DecoInner>

          <div className="h-[92px] flex justify-end mt-[30px] mb-[60px] pr-4">
            <SpeechText className="text-white text-[12px] font-semibold h-[52px] w-[233px] text-center">
              몽이는 예지력을 갈고 닦아서 돌아오겠다몽! <br />
              그때까지 새해 복 많이 받으라몽~
            </SpeechText>
            <Image src={'/img/fortune/prophet_mong.png'} alt="예언자몽" width={91} height={92} />
          </div>
        </div>
      </section>
      <footer className="py-[30px] px-10 bg-[#ECEFF5]">
        <p className="kcc-ganpan text-gray-2 text-center text-[12px]">이벤트 유의사항</p>

        <ul className="*:list-disc-v2 text-gray-3 text-[10px] mt-5 flex flex-col gap-2">
          <li>
            본 콘텐츠는 운세 서비스를 제공하기 위해 참고용으로 작성된 것이며 장사에 대한 전문적인
            정보를 제공하는 것은 아닙니다.
          </li>
          <li>
            신규 사장님의 매장 연결 완료 시 이벤트 참여가 완료되며, 이벤트 기간 내 가입 후 탈퇴,
            혹은 본인 매장이 아닌 매장 등록 시 확인 후 리워드 지급에서 제외될 수 있습니다.
          </li>
          <li>
            이벤트 경품은 댓글몽 이용 이력이 없는 사업자번호 등록 계정 중 매장 연결이 완료된 계정
            대상으로 2월 14일 일괄 지급됩니다.
          </li>
          <li>
            이벤트 경품은 <b>{'[내 정보 관리] > [계정 정보]에 등록된 휴대폰 번호'}</b>로 발송되며,
            오발송 시 다시 발송이 어려우니 휴대폰 번호를 미리 확인해주시기 바랍니다.
          </li>
          <li>본 이벤트는 당사 내부 사정에 의해 예고없이 변경 또는 취소될 수 있습니다.</li>
        </ul>
      </footer>
    </>
  );
};

const DecoInner = ({ children, className = '' }: { children?: ReactNode; className?: string }) => {
  return (
    <InnerContainer className={cn('relative min-h-32', className)}>
      <div className="flex justify-between">
        <Deco />

        <Deco className="rotate-90" />
      </div>
      {children}
      <div className="flex justify-between">
        <Deco className="rotate-[270deg]" />
        <Deco className="rotate-180" />
      </div>
    </InnerContainer>
  );
};

const InnerContent = twc.p`text-[#444] text-[15px]`;
const InnerContainer = twx.div`${styles.text_container} p-5`;
const InnerTitle = twc.h2`text-[15px] text-[#F67C38] kcc-ganpan text-center text-balance`;

const ContentTitle = twc.h3`text-[#444] text-[15px] font-bold`;
const Content = twx.p`text-[#444] text-[15px] pl-4 mt-0.5 leading-tight`;

const SubTitle = twx.p`text-[#4d75e4] kcc-ganpan text-[18px] leading-tight ${styles['-letter']}`;
const Title = twx.p`kcc-ganpan text-[32px] leading-tight ${styles['-letter-tight']}`;

const Fortune3Wrapper = () => {
  const [fortuneData] = useFortuneFormData();
  const { data: fortune } = useFortune({
    shopName: fortuneData.shopName,
    openedAt: fortuneData.openedAt,
  });

  if (!fortune) {
    return <></>;
  }

  return <FortunePage3 fortune={fortune} />;
};

export default Fortune3Wrapper;
